import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { fetchDownloads, type DownloadItem } from "../../api";

export default function Downloads() {
  const [items, setItems] = useState<DownloadItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchDownloads()
      .then((res) => {
        setItems(res.items || []);
        if (res.categories && res.categories.length > 0) {
          setCategories(["All", ...res.categories.map((c) => c.name)]);
        }
      })
      .catch((err) => {
        console.warn("下载数据加载失败:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20technical%20documents%20and%20manuals%20stacked%20on%20modern%20desk%20with%20tablet%20and%20radio%20equipment%20warm%20lighting%20clean%20workspace%20high%20quality%20product%20photography%20overhead%20view%20technology%20download%20center%20theme&width=1400&height=800&seq=200&orientation=landscape"
              alt="Downloads"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Resources</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Download<br />
                <span className="text-secondary">Center</span>
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Access product catalogs, datasheets, user manuals, certifications, and more.
              </p>
            </div>
          </div>
        </section>

        {/* Download List */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Category Filter */}
            {categories.length > 1 && (
              <div className="flex flex-wrap items-center gap-3 mb-10">
                <span className="text-gray-500 text-sm mr-2">Filter:</span>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-20">
                <div className="inline-block w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                <p className="text-gray-500 mt-4">Loading downloads...</p>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-gray-500">Failed to load downloads. Please try again later.</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && items.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-50 rounded-full flex items-center justify-center">
                  <i className="ri-file-download-line text-gray-300 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-400 mb-2">No downloads available</h3>
                <p className="text-gray-400 text-sm">Downloadable resources will appear here once added.</p>
              </div>
            )}

            {/* Download Grid */}
            {!loading && items.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="group bg-white rounded-xl border border-gray-100 hover:border-primary/30 transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-primary text-xl`}></i>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 bg-light-gray text-gray-500 text-xs font-medium rounded-full">
                          {item.fileType}
                        </span>
                      </div>
                      <h3 className="text-dark font-bold text-base mb-2 leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4">
                        {item.description}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-gray-400 text-xs">{item.fileSize}</span>
                        {item.file_url ? (
                          <a
                            href={item.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
                          >
                            <i className="ri-download-line"></i>
                            Download
                          </a>
                        ) : (
                          <button
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-400 text-sm font-medium rounded-full cursor-not-allowed whitespace-nowrap"
                          >
                            <i className="ri-download-line"></i>
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="bg-light-gray py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
              Need a Specific Document?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Can&apos;t find what you are looking for? Contact our team and we will provide the resources you need.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-3 px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}